/**
 * The AI's highlight picks as a list: what the tool found, why it scored, and one click to watch
 * each. Before this the picks only existed as blocks on the timeline, with no title or score in
 * sight, so the main thing the analysis produces was the hardest thing to see.
 */
import { Play, Check, Sparkles } from 'lucide-react'
import type { Segment, Transcript } from '../types'

interface Props {
  segments: Segment[]
  transcript: Transcript | null
  selectedSegments: string[]
  currentTime: number
  onToggleSelect: (id: string) => void
  onSelectAll: () => void
  onPlay: (seg: Segment) => void
  onExport: () => void
}

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`

export function HighlightsPanel({ segments, transcript, selectedSegments, currentTime, onToggleSelect, onSelectAll, onPlay, onExport }: Props) {
  const picks = [...segments].sort((a, b) => a.start - b.start)
  if (!picks.length) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 text-center text-sm text-gray-500">
        Highlights appear here once the video is analyzed.
      </div>
    )
  }
  const allSelected = picks.every(p => selectedSegments.includes(p.id))
  // The first line or two of what is said inside a pick, so a person can judge it without playing it.
  const quote = (seg: Segment) => {
    const lines = (transcript?.sentences || []).filter(s => s.end > seg.start && s.start < seg.end).map(s => s.text)
    const text = lines.join(' ')
    return text.length > 150 ? text.slice(0, 147).replace(/\s+\S*$/, '') + '…' : text
  }
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Sparkles size={13} className="text-indigo-400" />
          <span>{picks.length} highlight{picks.length === 1 ? '' : 's'} found</span>
        </div>
        <button onClick={onSelectAll} className="text-xs text-indigo-300 hover:text-white">
          {allSelected ? 'All selected' : 'Select all'}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
        {picks.map((seg, i) => {
          const selected = selectedSegments.includes(seg.id)
          const playingNow = currentTime >= seg.start && currentTime <= seg.end
          const score = typeof seg.highlight_score === 'number' ? Math.round(seg.highlight_score) : null
          return (
            <div key={seg.id}
              className={`rounded-xl border p-3 transition-colors ${playingNow ? 'border-indigo-400/60 bg-indigo-500/10' : selected ? 'border-white/20 bg-white/[0.06]' : 'border-white/10 bg-white/[0.03]'}`}>
              <div className="flex items-start gap-2">
                <span className="mt-1 w-1.5 self-stretch rounded-full shrink-0" style={{ background: seg.color || '#8B5CF6' }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-mono text-gray-500">#{i + 1} · {fmt(seg.start)}–{fmt(seg.end)} · {Math.round(seg.end - seg.start)}s</span>
                    {score !== null && (
                      <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/20" title="How strong the tool rated this moment, out of 100">
                        score {score}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-sm font-semibold text-gray-100 leading-snug">{seg.text}</div>
                  {quote(seg) && <p className="mt-1 text-xs text-gray-400 leading-relaxed">“{quote(seg)}”</p>}
                  <div className="mt-2 flex items-center gap-2">
                    <button onClick={() => onPlay(seg)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-white/10 hover:bg-white/15 text-white">
                      <Play size={12} /> Play clip
                    </button>
                    <button onClick={() => onToggleSelect(seg.id)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border ${selected ? 'border-indigo-400/50 bg-indigo-500/20 text-indigo-100' : 'border-white/15 text-gray-300 hover:text-white'}`}>
                      <Check size={12} /> {selected ? 'In export' : 'Add to export'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <div className="p-3 border-t border-white/5">
        <button onClick={onExport} disabled={!selectedSegments.length}
          className="w-full py-2 text-sm rounded-lg bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
          {selectedSegments.length ? `Export ${selectedSegments.length} short${selectedSegments.length === 1 ? '' : 's'} →` : 'Pick a highlight to export'}
        </button>
      </div>
    </div>
  )
}
